import { useEffect, useMemo, useState } from "react";
import { format, isToday } from "date-fns";
import { Copy, Pencil, Printer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { usePermission } from "@/hooks/usePermission";
import { useAuth } from "@/context/AuthContext";
import { canEditPrescription } from "@/lib/prescriptionEdit";
import { useFilledCustomFields } from "@/hooks/usePrescriptionLayout";
import { formatDate } from "@/lib/formatDate";
import { cn } from "@/lib/utils";
import { getReadingHistory, type Reading, type ReadingCode, type VisitRecord } from "@/lib/api";
import { examSigns, flagFor, flagTone, fmtNum, fmtTime, shortDate } from "@/lib/vitals";
import Sparkline from "@/components/Vitals/Sparkline";
import type { Prescription } from "@/types/prescription";
import type { Appointment } from "@/types";
import EditAppointmentModal from "@/components/Appointment/EditAppointmentModal";

type OverviewVisit = { id: string; date: string; prescription?: Prescription };

interface Props {
  patientId: string;
  /** Newest first */
  visits: OverviewVisit[];
  visitRecords: VisitRecord[];
  visitCount: number;
  ageMonths: number | null;
  onViewPrescription: (rx: Prescription) => void;
  /** Absent when the user cannot write prescriptions */
  onCopyPrescription?: (rx: Prescription) => void;
  /** Absent when the user cannot write prescriptions; shown only while the Rx is still editable */
  onEditPrescription?: (rx: Prescription) => void;
  /** Switch to another tab of the patient page ("prescriptions", "vitals") */
  onOpenTab: (tab: string) => void;
  appointments: Appointment[];
  /** Reload after an appointment is rescheduled or cancelled */
  onAppointmentsChanged: () => void;
  /** Free text from the patient record, e.g. "Metformin 500 mg 1-0-1, Telmisartan 40 mg" */
  currentMedications?: string | null;
}

const EARLIER = 5;
const UPCOMING = 2;

// Appointment date is a calendar day ("2026-10-04" or "2026-10-04T00:00:00Z"): read it as a local date
const aptDay = (a: Appointment) => {
  const d = (a.date ?? "").split("T")[0];
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) return null;
  const [y, m, day] = d.split("-").map(Number);
  return new Date(y, m - 1, day);
};
const aptTime = (t?: string) => {
  const m = (t ?? "").match(/^(\d{1,2}):(\d{2})/);
  if (!m) return null;
  const d = new Date(); d.setHours(+m[1], +m[2], 0, 0);
  return format(d, "h:mm a");
};

const VITALS: { code: "bp" | Exclude<ReadingCode, "bp_systolic" | "bp_diastolic">; label: string; unit: string }[] = [
  { code: "bp", label: "BP", unit: "mmHg" },
  { code: "pulse", label: "Pulse", unit: "bpm" },
  { code: "spo2", label: "SpO2", unit: "%" },
  { code: "temp", label: "Temp", unit: "°F" },
  { code: "weight", label: "Weight", unit: "kg" },
  { code: "rbs", label: "RBS", unit: "mg/dL" },
];

const dash = <span className="text-muted-foreground/60">—</span>;

const timeOf = (iso?: string) => {
  const d = iso ? new Date(iso) : null;
  return d && !isNaN(d.getTime()) && iso!.includes("T")
    ? d.toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit" }).toUpperCase()
    : null;
};

/**
 * The patient page's first tab: the last visit in full, earlier visits one line each, and how the
 * vitals are trending — what the doctor needs before calling the patient in.
 */
export default function PatientOverviewTab({
  patientId, visits, visitRecords, visitCount, ageMonths, onViewPrescription, onCopyPrescription, onEditPrescription, onOpenTab,
  appointments, onAppointmentsChanged, currentMedications,
}: Props) {
  const { can } = usePermission();
  const { currentUser } = useAuth();
  // One medicine per line: the record is free text, separated by commas, semicolons or new lines
  const longTermMeds = (currentMedications ?? "").split(/[,;\n]+/).map(m => m.trim()).filter(Boolean);
  const withRx = visits.filter(v => v.prescription);
  const last = withRx[0]?.prescription;
  const customFields = useFilledCustomFields(last?.custom_fields);
  const examination = [examSigns(last?.vitals), last?.examination?.trim()].filter(Boolean).join("\n");
  // When it was saved, shown only if saved on its own date: a back-dated record's time is when it was typed in
  const dayOf = (iso?: string) => (iso ? format(new Date(iso), "yyyy-MM-dd") : "");
  const lastTime = last?.created_at && dayOf(last.created_at) === dayOf(last.date) ? timeOf(last.created_at) : null;
  const earlier = withRx.slice(1, 1 + EARLIER);

  // Doctor who saw the patient on the last visit, from the visit record that holds that prescription
  const lastDoctor = useMemo(() => {
    const name = last?.doctor_name || visitRecords.find(r => last?.id && r.prescriptionIds.includes(last.id))?.doctorName;
    return name ? `Dr. ${name.replace(/^dr\.?\s*/i, "")}` : null;
  }, [last, visitRecords]);

  // Vitals: latest value and trend of each, from every recorded reading
  const [readings, setReadings] = useState<Reading[] | null>(null);
  useEffect(() => {
    let cancelled = false;
    getReadingHistory(patientId, 400)
      .then(r => { if (!cancelled) setReadings(r.readings); })
      .catch(() => { if (!cancelled) setReadings([]); });
    return () => { cancelled = true; };
  }, [patientId]);

  const trends = useMemo(() => {
    const byCode: Partial<Record<ReadingCode, Reading[]>> = {};
    // oldest → newest
    for (const r of [...(readings ?? [])].reverse()) if (r.status === "final") (byCode[r.code] ??= []).push(r);
    return VITALS.map(v => {
      if (v.code === "bp") {
        const sys = byCode.bp_systolic ?? [], dia = byCode.bp_diastolic ?? [];
        if (!sys.length) return null;
        const s = sys[sys.length - 1];
        const d = dia.find(x => x.readingGroup === s.readingGroup);
        return {
          ...v, count: sys.length, at: s.effectiveAt,
          value: d ? `${fmtNum(s.value)}/${fmtNum(d.value)}` : fmtNum(s.value),
          flag: flagFor(s, ageMonths) ?? flagFor(d, ageMonths),
          series: [sys.slice(-12).map(r => r.value), dia.slice(-12).map(r => r.value)],
        };
      }
      const list = byCode[v.code] ?? [];
      if (!list.length) return null;
      const latest = list[list.length - 1];
      return { ...v, count: list.length, at: latest.effectiveAt, value: fmtNum(latest.value), flag: flagFor(latest, ageMonths), series: [list.slice(-12).map(r => r.value)] };
    }).filter(Boolean) as { code: string; label: string; unit: string; count: number; at: string; value: string; flag: "H" | "L" | null; series: number[][] }[];
  }, [readings, ageMonths]);
  const readingCount = Math.max(0, ...trends.map(t => t.count));

  // Appointments: what's coming up (soonest first), and how often the patient hasn't turned up —
  // one still "scheduled" after its time counts as missed, as on the doctor's home
  const [editingApt, setEditingApt] = useState<Appointment | null>(null);
  const { upcoming, missed } = useMemo(() => {
    const now = new Date();
    // When it is due: the day at its time, or the end of the day when no time was set
    const due = (a: Appointment, day: Date) => {
      const m = (a.time ?? "").match(/^(\d{1,2}):(\d{2})/);
      const d = new Date(day);
      if (m) d.setHours(+m[1], +m[2], 0, 0); else d.setHours(23, 59, 59, 999);
      return d;
    };
    const withDay = appointments
      .map(a => { const day = aptDay(a); return day ? { a, day, at: due(a, day) } : null; })
      .filter((x): x is { a: Appointment; day: Date; at: Date } => !!x);
    // Checked in today stays "upcoming" even after its time: the patient is here
    const up = withDay
      .filter(({ a, at, day }) => (a.status === "scheduled" && at >= now) || (a.status === "checked_in" && day.toDateString() === now.toDateString()))
      .sort((x, y) => x.at.getTime() - y.at.getTime());
    // A booking left "scheduled" on a day the patient did come in (a visit or prescription that day) isn't missed
    const dayKey = (d: Date) => format(d, "yyyy-MM-dd");
    const cameIn = new Set(
      [...visitRecords.map(v => v.visitDate || v.date), ...visits.map(v => v.prescription?.date || v.date)]
        .map(d => { const x = d ? new Date(d.length === 10 ? `${d}T00:00:00` : d) : null; return x && !isNaN(x.getTime()) ? dayKey(x) : null; })
        .filter(Boolean) as string[]
    );
    const miss = withDay
      .filter(({ a, at, day }) => a.status === "no_show" || (a.status === "scheduled" && at < now && !cameIn.has(dayKey(day))))
      .sort((x, y) => y.at.getTime() - x.at.getTime());
    return { upcoming: up, missed: miss };
  }, [appointments, visits, visitRecords]);

  const complaints = (last?.complaints ?? [])
    .map(c => [c.complaint, c.duration].filter(Boolean).join(", "))
    .filter(Boolean).join(" · ");
  const tests = (last?.investigations ?? []).filter(i => i?.trim()).join(", ");
  const medicines = (last?.medicines ?? []).filter(m => (m.product || m.brand_name)?.trim());

  return (
    <div className="grid gap-5 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)] items-start">
      <div className="space-y-5 min-w-0">

        {/* Last visit */}
        <section aria-label="Last visit" className="rounded-lg border border-border bg-card overflow-hidden">
          {!last ? (
            <p className="px-5 py-8 text-sm text-muted-foreground text-center">No prescriptions yet.</p>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-5 py-3.5 border-b border-border">
                <h2 className="text-[15px] font-semibold text-foreground">Last visit · {formatDate(last.date)}</h2>
                {(lastDoctor || lastTime) && (
                  <span className="text-[13px] text-muted-foreground">
                    {[lastDoctor, lastTime].filter(Boolean).join(" · ")}
                  </span>
                )}
                <div className="flex items-center gap-2 ml-auto">
                  {/* Same day, same doctor (as the server allows); later changes are a copy */}
                  {onEditPrescription && canEditPrescription(last, currentUser) && (
                    <Button type="button" variant="outline" size="sm" onClick={() => onEditPrescription(last)} className="gap-1.5">
                      <Pencil className="w-3.5 h-3.5" /> Edit
                    </Button>
                  )}
                  <Button type="button" variant="outline" size="sm" onClick={() => onViewPrescription(last)} className="gap-1.5">
                    <Printer className="w-3.5 h-3.5" /> Print
                  </Button>
                  {onCopyPrescription && (
                    <Button type="button" variant="outline" size="sm" onClick={() => onCopyPrescription(last)} className="gap-1.5">
                      <Copy className="w-3.5 h-3.5" /> Copy Rx
                    </Button>
                  )}
                </div>
              </div>

              <dl className="grid gap-4 sm:grid-cols-3 px-5 py-4 border-b border-border">
                <div className="min-w-0">
                  <dt className="text-xs text-muted-foreground">Complaints</dt>
                  <dd className="mt-1 text-sm text-foreground">{complaints || dash}</dd>
                </div>
                <div className="min-w-0">
                  <dt className="text-xs text-muted-foreground">Diagnosis</dt>
                  <dd className="mt-1 text-sm font-semibold text-foreground">{last.diagnosis?.trim() || dash}</dd>
                </div>
                <div className="min-w-0">
                  <dt className="text-xs text-muted-foreground">Tests</dt>
                  <dd className="mt-1 text-sm text-foreground">{tests || dash}</dd>
                </div>
              </dl>

              {examination && (
                <div className="px-5 py-3 border-b border-border">
                  <p className="text-xs text-muted-foreground">Examination</p>
                  <p className="mt-1 text-sm text-foreground whitespace-pre-line line-clamp-3">{examination}</p>
                </div>
              )}

              {medicines.length === 0 ? (
                <p className="px-5 py-4 text-sm text-muted-foreground">No medicines prescribed.</p>
              ) : (
                <>
                {/* Phones: one row per medicine, no sideways scrolling */}
                <ul className="sm:hidden divide-y divide-divider">
                  {medicines.map((m, i) => (
                    <li key={i} className="flex gap-3 px-5 py-2.5 text-sm">
                      <span className="w-4 flex-shrink-0 text-muted-foreground tabular-nums">{i + 1}</span>
                      <div className="min-w-0">
                        <span className="block font-medium text-foreground">{m.product || m.brand_name}</span>
                        {m.generic_name && <span className="block text-xs text-muted-foreground">{m.generic_name}</span>}
                        <span className="block mt-0.5 text-[13px] text-muted-foreground">
                          {[m.dosage && <span key="d" className="font-mono text-foreground">{m.dosage}</span>, m.taken_when, m.duration, m.quantity && `Qty ${m.quantity}`]
                            .filter(Boolean).map((part, j) => <span key={j}>{j > 0 && " · "}{part}</span>)}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
                <div className="hidden sm:block">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="h-8 bg-muted/50 border-b border-border text-left text-2xs font-medium uppercase tracking-wide text-muted-foreground">
                        <th className="pl-5 pr-2 w-10 font-medium">#</th>
                        <th className="px-2 font-medium">Medicine</th>
                        <th className="px-2 font-medium">Dose</th>
                        <th className="px-2 font-medium">When</th>
                        <th className="px-2 font-medium">Duration</th>
                        <th className="pl-2 pr-5 font-medium text-right">Qty</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-divider">
                      {medicines.map((m, i) => (
                        <tr key={i} className="align-top">
                          <td className="pl-5 pr-2 py-2 text-muted-foreground tabular-nums">{i + 1}</td>
                          <td className="px-2 py-2">
                            <span className="block font-medium text-foreground">{m.product || m.brand_name}</span>
                            {m.generic_name && <span className="block text-xs text-muted-foreground">{m.generic_name}</span>}
                          </td>
                          <td className="px-2 py-2 font-mono text-[13px] whitespace-nowrap">{m.dosage || dash}</td>
                          <td className="px-2 py-2 text-muted-foreground">{m.taken_when || dash}</td>
                          <td className="px-2 py-2 text-muted-foreground whitespace-nowrap">{m.duration || dash}</td>
                          <td className="pl-2 pr-5 py-2 text-right font-mono text-[13px] tabular-nums">{m.quantity || dash}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                </>
              )}

              {last.advice?.trim() && (
                <div className="px-5 py-3 border-t border-border">
                  <p className="text-xs text-muted-foreground">Advice</p>
                  <p className="mt-1 text-sm text-foreground whitespace-pre-line line-clamp-3">{last.advice}</p>
                </div>
              )}

              {customFields.map((cf) => (
                <div key={cf.key} className="px-5 py-3 border-t border-border">
                  <p className="text-xs text-muted-foreground">{cf.label}</p>
                  <p className="mt-1 text-sm text-foreground whitespace-pre-line line-clamp-3">{cf.value}</p>
                </div>
              ))}
            </>
          )}
        </section>

        {/* Earlier visits — one line each */}
        {earlier.length > 0 && (
          <section aria-label="Earlier visits" className="rounded-lg border border-border bg-card overflow-hidden">
            <div className="flex items-center justify-between gap-3 px-5 py-3.5 border-b border-border">
              <h2 className="text-[15px] font-semibold text-foreground">Earlier visits</h2>
              <button type="button" onClick={() => onOpenTab("prescriptions")} className="touch-target text-[13px] font-medium text-primary hover:underline">
                All {visitCount} visits
              </button>
            </div>
            <ul className="divide-y divide-divider">
              {earlier.map(v => {
                const rx = v.prescription!;
                const meds = (rx.medicines ?? []).map(m => m.product || m.brand_name).filter(Boolean).join(", ");
                return (
                  <li key={v.id} className="grid grid-cols-[6.5rem_minmax(0,1fr)_auto] sm:grid-cols-[7rem_minmax(0,1.2fr)_minmax(0,2fr)_auto] items-center gap-3 px-5 min-h-10 py-2 text-sm">
                    <span className="text-muted-foreground tabular-nums">{formatDate(rx.date || v.date)}</span>
                    <span className="font-medium text-foreground truncate">{rx.diagnosis?.trim() || <span className="font-normal text-muted-foreground">No diagnosis</span>}</span>
                    <span className="hidden sm:block text-muted-foreground truncate" title={meds}>{meds || "—"}</span>
                    <button type="button" onClick={() => onViewPrescription(rx)} className="touch-target text-[13px] font-medium text-primary hover:underline">
                      Open
                    </button>
                  </li>
                );
              })}
            </ul>
          </section>
        )}
      </div>

      {/* Tablets: these cards side by side below the visits; desktop: the right-hand column */}
      <div className="grid gap-5 min-w-0 md:grid-cols-2 lg:grid-cols-1 items-start">
      {/* Vitals trend */}
      <section aria-label="Vitals" className="rounded-lg border border-border bg-card overflow-hidden min-w-0">
        <div className="flex items-center justify-between gap-3 px-5 py-3.5 border-b border-border">
          <h2 className="text-[15px] font-semibold text-foreground">Vitals</h2>
          {trends.length > 0 && (
            <button type="button" onClick={() => onOpenTab("vitals")} className="touch-target text-xs text-muted-foreground hover:text-foreground">
              {readingCount > 1 ? `Last ${Math.min(readingCount, 12)} readings` : "All readings"}
            </button>
          )}
        </div>
        {readings === null ? (
          <p className="px-5 py-6 text-sm text-muted-foreground text-center">Loading…</p>
        ) : trends.length === 0 ? (
          <p className="px-5 py-6 text-sm text-muted-foreground text-center">No vitals recorded yet.</p>
        ) : (
          <ul className="divide-y divide-divider">
            {trends.map(t => {
              // When the latest reading was taken: an old one must not pass for today's
              const today = isToday(new Date(t.at));
              return (
                <li key={t.code} className="grid grid-cols-[3.5rem_minmax(0,1fr)_auto_2.75rem] items-center gap-3 px-5 h-12">
                  <span className="min-w-0 leading-tight">
                    <span className="block text-[13px] text-muted-foreground">{t.label}</span>
                    <span className="block text-2xs text-muted-foreground whitespace-nowrap">{today ? fmtTime(t.at) : shortDate(t.at)}</span>
                  </span>
                  {t.series[0].length >= 2 ? <Sparkline series={t.series} /> : <span />}
                  <span className={cn("text-right font-mono text-[15px] font-semibold tabular-nums",
                    today ? cn("text-foreground", flagTone(t.flag, t.code)) : "text-muted-foreground")}>
                    {t.value}
                  </span>
                  <span className="text-xs text-muted-foreground">{t.unit}</span>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* Long-term medicines — from the patient record (Edit patient → Current medicines) */}
      {longTermMeds.length > 0 && (
        <section aria-label="Long-term medicines" className="rounded-lg border border-border bg-card overflow-hidden min-w-0">
          <div className="px-5 py-3.5 border-b border-border">
            <h2 className="text-[15px] font-semibold text-foreground">Long-term medicines</h2>
          </div>
          <ul className="divide-y divide-divider">
            {longTermMeds.map((m, i) => (
              <li key={i} className="px-5 py-2 text-sm font-medium text-foreground">{m}</li>
            ))}
          </ul>
        </section>
      )}

      {/* Appointments — next ones with Reschedule; missed ones counted */}
      <section aria-label="Appointments" className="rounded-lg border border-border bg-card overflow-hidden min-w-0">
        <div className="flex items-center justify-between gap-3 px-5 py-3.5 border-b border-border">
          <h2 className="text-[15px] font-semibold text-foreground">Appointments</h2>
          {upcoming.length > UPCOMING && (
            <span className="text-xs text-muted-foreground">{upcoming.length} upcoming</span>
          )}
        </div>
        {upcoming.length === 0 ? (
          <p className="px-5 py-4 text-sm text-muted-foreground">No upcoming appointments.</p>
        ) : (
          <ul className="divide-y divide-divider">
            {upcoming.slice(0, UPCOMING).map(({ a, day }) => (
              <li key={a.id} className="flex items-center gap-3 px-5 py-2.5">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-foreground tabular-nums">
                    {format(day, "EEE, dd MMM yyyy")}
                    {aptTime(a.time) && <span className="font-normal text-muted-foreground"> · {aptTime(a.time)}</span>}
                  </p>
                  {(a.reason || a.status === "checked_in") && (
                    <p className="text-xs text-muted-foreground truncate">
                      {[a.status === "checked_in" ? "Checked in" : null, a.reason || null].filter(Boolean).join(" · ")}
                    </p>
                  )}
                </div>
                {can('appointments:write') && (
                  <Button type="button" variant="outline" size="sm" onClick={() => setEditingApt(a)} className="flex-shrink-0">
                    Reschedule
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
        {missed.length > 0 && (
          <p className="px-5 py-2.5 border-t border-border text-xs text-highlight-foreground">
            Missed {missed.length === 1 ? "1 appointment" : `${missed.length} appointments`} · last on {format(missed[0].day, "dd MMM yyyy")}
          </p>
        )}
      </section>
      </div>

      {editingApt && (
        <EditAppointmentModal
          isOpen
          appointment={editingApt}
          onClose={() => setEditingApt(null)}
          onUpdated={() => { setEditingApt(null); onAppointmentsChanged(); }}
        />
      )}
    </div>
  );
}
